import React, { useState, useCallback, useEffect } from 'react'
import { Card, Collapse, Spin, Empty, Tag, Space, Button, Select, DatePicker, Input, message, Modal, Form, InputNumber, Popconfirm, Tooltip, Row, Col, theme } from 'antd'
import VoiceTextarea from './VoiceTextarea'
import {
  DownloadOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  FilterOutlined,
  ReloadOutlined,
  ToolOutlined,
  SearchOutlined,
  LinkOutlined,
  ApartmentOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import NuovoInterventoModal, { type NuovoInterventoData } from './NuovoInterventoModal'
import RdaInterventoModal from './RdaInterventoModal'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { usePermission } from '@/hooks/usePermission'
import AssegnaOfficinaModal from './AssegnaOfficinaModal'
import type { Rda } from '@/hooks/useRdaIntervento'
import type { WorkOrder, Intervention, InterventionStatusType, PurchaseRequest } from '@/types'
import { WORK_ORDER_STATUS_COLORS as statusColors, INTERVENTION_TYPE_COLORS as interventionTypeColors } from '@/constants/statuses'
import { useAuth } from '@/contexts/AuthContext'
import { TagArticoliResi } from '@/components/TagArticoliResi'

// Ruoli amministrativi/gestionali che devono vedere anche le schede senza interventi
const RUOLI_GESTIONALI = ['ADMIN', 'GM', 'ACC', 'MAG']

// Modalità risoluzione
const resolutionModes: Record<number, string> = {
  1: 'Riparazione',
  2: 'Acquisto',
}

export const WorkOrderInterventionsPanel: React.FC = () => {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEditStato = usePermission('work_orders.edit') || usePermission('interventions.update_status')
  const isRuoloGestionale = RUOLI_GESTIONALI.includes(user?.ruolo as string)
  const isWorkshopRole = user?.ruolo === 'WORKSHOP'
  const canAssign = usePermission('workshop_quotes.request') && !isWorkshopRole
  // Copertura sinistro: modificabile solo da amministrazione/direzione
  const canEditSinistro = usePermission('work_orders.edit')
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [workOrderDetails, setWorkOrderDetails] = useState<Map<number, WorkOrder>>(new Map())
  const [workOrderRdas, setWorkOrderRdas] = useState<Map<number, Rda[]>>(new Map())
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set())
  const [interventionStatuses, setInterventionStatuses] = useState<InterventionStatusType[]>([])
  const [savingStatoId, setSavingStatoId] = useState<number | null>(null)
  const [notaModal, setNotaModal] = useState<{ workOrderId: number; interventionId: number; statoId: number } | null>(null)
  const [notaText, setNotaText] = useState('')
  
  // Filtri
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [searchText, setSearchText] = useState<string>('')
  const [dateFilter, setDateFilter] = useState<dayjs.Dayjs | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  
  // Modal modifica intervento
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingIntervention, setEditingIntervention] = useState<Intervention | null>(null)
  const [editingWorkOrderId, setEditingWorkOrderId] = useState<number | null>(null)
  const [form] = Form.useForm()
  // Modal conferma RDA quando si cambia modalità da Acquisto
  const [pendingInterventionSave, setPendingInterventionSave] = useState<any | null>(null)
  // Assegnazione officina
  const [assignWorkshopModal, setAssignWorkshopModal] = useState<{ workOrderId: number; interventionId: number; currentWorkshopId: number | null; interventions: Intervention[] } | null>(null)

  // Modal nuovo intervento con catalogo
  const [isNuovoInterventoOpen, setIsNuovoInterventoOpen] = useState(false)
  const [nuovoInterventoWorkOrderId, setNuovoInterventoWorkOrderId] = useState<number | null>(null)
  const [rdaAfterNew, setRdaAfterNew] = useState<{ open: boolean; interventionId: number; workOrderId: number; descrizione: string; note: string | null }>({ open: false, interventionId: 0, workOrderId: 0, descrizione: '', note: null })

  // Carica schede lavoro attive (escludi chiuse)
  const { data: workOrdersData, isLoading: workOrdersLoading, refetch: refetchWorkOrders } = useWorkOrders(1, 200)
  
  // Carica stati intervento
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.SYSTEM_INTERVENTION_STATUS_TYPES)
        setInterventionStatuses(res.data.items || res.data || [])
      } catch (error) {
        console.error('Errore caricamento stati intervento:', error)
      }
    }
    fetchStatuses()
  }, [])


  const handleSaveStato = async (workOrderId: number, interventionId: number, statoId: number, nota?: string) => {
    const status = interventionStatuses.find((s: InterventionStatusType) => s.id === statoId)
    if (status?.richiede_nota && !nota) {
      setNotaText('')
      setNotaModal({ workOrderId, interventionId, statoId })
      return
    }
    setSavingStatoId(interventionId)
    try {
      await axiosInstance.patch(
        `${API_ENDPOINTS.WORK_ORDERS}/${workOrderId}/interventions/${interventionId}/status`,
        { stato_intervento_id: statoId, ...(nota ? { note_sospensione: nota } : {}) }
      )
      const res = await axiosInstance.get(`${API_ENDPOINTS.WORK_ORDERS}/${workOrderId}`)
      setWorkOrderDetails(prev => new Map(prev).set(workOrderId, res.data))
    } catch {
      message.error('Salvataggio stato fallito')
    } finally {
      setSavingStatoId(null)
    }
  }

  const handleNotaConfirm = () => {
    if (!notaModal) return
    if (!notaText.trim()) { message.warning('Inserisci un motivo'); return }
    const { workOrderId, interventionId, statoId } = notaModal
    setNotaModal(null)
    handleSaveStato(workOrderId, interventionId, statoId, notaText.trim())
  }

  // Carica dettagli work order
  const loadWorkOrderDetail = useCallback(async (woId: number) => {
    if (workOrderDetails.has(woId)) return
    
    try {
      setLoadingDetails(prev => new Set(prev).add(woId))
      const [response, rdasRes] = await Promise.all([
        axiosInstance.get(`${API_ENDPOINTS.WORK_ORDERS}/${woId}`),
        axiosInstance.get(`/rdas/work-order/${woId}`).catch(() => ({ data: [] })),
      ])
      setWorkOrderDetails(prev => new Map(prev).set(woId, response.data))
      setWorkOrderRdas(prev => new Map(prev).set(woId, rdasRes.data ?? []))
    } catch (error) {
      console.error(`Errore caricamento work order ${woId}:`, error)
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev)
        newSet.delete(woId)
        return newSet
      })
    }
  }, [workOrderDetails])

  // Precarica dettagli al caricamento
  useEffect(() => {
    if (!workOrdersData?.items) return
    
    const loadBatches = async () => {
      const items = workOrdersData.items.filter(wo => !['conclusa', 'annullata'].includes(wo.stato))
      const batchSize = 5
      
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize)
        await Promise.all(batch.map(wo => loadWorkOrderDetail(wo.id)))
      }
    }
    
    loadBatches()
  }, [workOrdersData?.items, loadWorkOrderDetail])

  // Gestione collapse
  const handleCollapseChange = (keys: string | string[]) => {
    const newKeys = Array.isArray(keys) ? keys : [keys]
    setExpandedKeys(newKeys)
  }

  // Espandi/Riduci tutto
  const expandAll = () => {
    const keys = filteredWorkOrders.map(wo => wo.id.toString())
    setExpandedKeys(keys)
  }

  const collapseAll = () => {
    setExpandedKeys([])
  }

  // Filtra schede lavoro
  const filteredWorkOrders = (workOrdersData?.items || [])
    // Escludi schede chiuse/concluse/annullate
    .filter(wo => !['conclusa', 'annullata'].includes(wo.stato))
    // Ruoli operativi: solo schede con almeno un intervento
    // Ruoli gestionali (ADMIN, GM, ACC, MAG): vedono anche le schede senza interventi
    .filter(wo => {
      if (isRuoloGestionale) return true
      const details = workOrderDetails.get(wo.id)
      return details?.interventions && details.interventions.length > 0
    })
    // Filtro stato
    .filter(wo => !statusFilter || wo.stato === statusFilter)
    // Filtro ricerca testo
    .filter(wo => {
      if (!searchText) return true
      const search = searchText.toLowerCase()
      return (
        wo.numero_scheda?.toLowerCase().includes(search) ||
        wo.customer_nome?.toLowerCase().includes(search) ||
        wo.vehicle_targa?.toLowerCase().includes(search) ||
        wo.vehicle_marca?.toLowerCase().includes(search) ||
        wo.vehicle_modello?.toLowerCase().includes(search)
      )
    })
    // Filtro data specifica
    .filter(wo => {
      if (!dateFilter) return true
      const woDate = dayjs(wo.data_appuntamento || wo.data_creazione || wo.created_at)
      return woDate.isSame(dateFilter, 'day')
    })
    // Ordinamento cronologico ascendente per data appuntamento
    .sort((a, b) => {
      const dateA = dayjs(a.data_appuntamento || a.data_creazione || a.created_at)
      const dateB = dayjs(b.data_appuntamento || b.data_creazione || b.created_at)
      return dateA.valueOf() - dateB.valueOf()
    })

  // Apri modal modifica intervento
  const handleEditIntervention = (intervention: Intervention, workOrderId: number) => {
    setEditingIntervention(intervention)
    setEditingWorkOrderId(workOrderId)
    form.setFieldsValue({
      descrizione_intervento: intervention.descrizione_intervento,
      durata_stimata: intervention.durata_stimata,
      tipo_intervento: intervention.tipo_intervento,
      modalita_risoluzione_id: intervention.modalita_risoluzione_id,
      stato_intervento_id: intervention.stato_intervento_id,
      note_intervento: intervention.note_intervento,
      ore_effettive: intervention.ore_effettive,
    })
    setIsEditModalOpen(true)
  }

  // Logica effettiva di salvataggio intervento
  const doSaveIntervention = async (values: any) => {
    if (!editingIntervention?.id || !editingWorkOrderId) return
    try {
      await axiosInstance.put(
        `${API_ENDPOINTS.WORK_ORDERS}/${editingWorkOrderId}/interventions/${editingIntervention.id}`,
        values
      )
      message.success('Intervento aggiornato')
      setIsEditModalOpen(false)
      setWorkOrderDetails(prev => {
        const newMap = new Map(prev)
        newMap.delete(editingWorkOrderId)
        return newMap
      })
      loadWorkOrderDetail(editingWorkOrderId)
    } catch (error: any) {
      console.error('Errore salvataggio:', error)
      message.error(`Errore: ${error.response?.data?.detail || error.message}`)
    }
  }

  // Salva intervento (con controllo RDA se si cambia modalità da Acquisto)
  const handleSaveIntervention = async (values: any) => {
    if (!editingIntervention?.id || !editingWorkOrderId) return

    const oldModalita = editingIntervention.modalita_risoluzione_id
    const newModalita = values.modalita_risoluzione_id
    const linkedRdas = (editingIntervention as any).purchase_requests || []

    // Se si cambia da Acquisto (2) a un'altra modalità e ci sono RDA collegate, chiedi conferma
    if (oldModalita === 2 && newModalita !== 2 && linkedRdas.length > 0) {
      setPendingInterventionSave(values)
      return
    }

    await doSaveIntervention(values)
  }

  // Elimina intervento
  const handleDeleteIntervention = async (interventionId: number, workOrderId: number) => {
    try {
      await axiosInstance.delete(
        `${API_ENDPOINTS.WORK_ORDERS}/${workOrderId}/interventions/${interventionId}`
      )
      message.success('Intervento eliminato')
      
      // Ricarica dettagli work order
      setWorkOrderDetails(prev => {
        const newMap = new Map(prev)
        newMap.delete(workOrderId)
        return newMap
      })
      loadWorkOrderDetail(workOrderId)
    } catch (error: any) {
      console.error('Errore eliminazione:', error)
      message.error(`Errore: ${error.response?.data?.detail || error.message}`)
    }
  }

  // Apri modale nuovo intervento
  const handleCreateIntervention = (workOrderId: number) => {
    setNuovoInterventoWorkOrderId(workOrderId)
    setIsNuovoInterventoOpen(true)
  }

  // Salva nuovo intervento dalla modale catalogo
  const handleNuovoInterventoConfirm = async (data: NuovoInterventoData) => {
    if (!nuovoInterventoWorkOrderId) return
    try {
      const res = await axiosInstance.post(
        `${API_ENDPOINTS.WORK_ORDERS}/${nuovoInterventoWorkOrderId}/interventions`,
        {
          descrizione_intervento: data.descrizione_intervento,
          durata_stimata: data.durata_stimata,
          tipo_intervento: data.tipo_intervento,
          modalita_risoluzione_id: data.modalita_risoluzione_id,
          note_intervento: data.note_intervento,
        }
      )
      setIsNuovoInterventoOpen(false)
      setWorkOrderDetails(prev => {
        const newMap = new Map(prev)
        newMap.delete(nuovoInterventoWorkOrderId)
        return newMap
      })
      loadWorkOrderDetail(nuovoInterventoWorkOrderId)
      const newId = res.data?.id
      const woId = nuovoInterventoWorkOrderId
      if (newId) {
        Modal.confirm({
          title: 'Intervento salvato',
          content: 'Vuoi gestire subito la Richiesta di Acquisto?',
          okText: 'Sì, gestisci RDA',
          cancelText: 'No, chiudi',
          onOk: () => setRdaAfterNew({ open: true, interventionId: newId, workOrderId: woId, descrizione: data.descrizione_intervento, note: data.note_intervento ?? null }),
        })
      }
    } catch (error: any) {
      console.error('Errore creazione:', error)
      message.error(`Errore: ${error.response?.data?.detail || error.message}`)
    }
  }

  // Naviga alla scheda lavoro
  const goToWorkOrder = (woId: number) => {
    navigate(`/schede-lavoro/${woId}`)
  }

  // Render dettaglio work order con interventi
  const renderWorkOrderDetail = (workOrderId: number) => {
    const details = workOrderDetails.get(workOrderId)
    const isLoading = loadingDetails.has(workOrderId)

    if (isLoading) {
      return <Spin size="small" />
    }

    if (!details) {
      return <Empty description="Caricamento..." />
    }

    const interventions = [...(details.interventions || [])].sort(
      (a, b) => (a.progressivo ?? a.id ?? 0) - (b.progressivo ?? b.id ?? 0)
    )

    const rdaMap: Record<number, Rda> = {}
    for (const r of workOrderRdas.get(workOrderId) ?? []) {
      rdaMap[r.intervention_id] = r
    }

    if (interventions.length === 0) {
      return (
        <div style={{ padding: '8px 0' }}>
          <Empty description="Nessun intervento" />
          {!isWorkshopRole && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleCreateIntervention(workOrderId)}
              style={{ marginTop: 8, width: '100%' }}
            >
              Aggiungi Intervento
            </Button>
          )}
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px 0' }}>
        {interventions.map((intervention: Intervention) => (
          <div 
            key={`int-${intervention.id}`} 
            style={{ 
              padding: '10px 12px',
              backgroundColor: 'var(--color-bg-subtle)',
              borderRadius: '6px',
              borderLeft: `3px solid ${interventionTypeColors[intervention.tipo_intervento] || '#1890ff'}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                {/* Titolo intervento */}
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {intervention.descrizione_intervento}
                </div>
                
                {/* Tags info */}
                <Space size={4} wrap>
                  <Tag color={interventionTypeColors[intervention.tipo_intervento]}>
                    {intervention.tipo_intervento}
                  </Tag>
                  
                  {intervention.modalita_risoluzione_id && (
                    <Tag color={intervention.modalita_risoluzione_id === 2 ? 'purple' : 'cyan'}>
                      {resolutionModes[intervention.modalita_risoluzione_id]}
                    </Tag>
                  )}
                  
                  <Select
                    size="small"
                    style={{ width: 130 }}
                    value={intervention.stato_intervento_id ?? undefined}
                    placeholder="Non iniziato"
                    disabled={!canEditStato || savingStatoId === intervention.id}
                    loading={savingStatoId === intervention.id}
                    onChange={(val: number) => handleSaveStato(workOrderId, intervention.id!, val)}
                    options={interventionStatuses.map((s: InterventionStatusType) => ({ value: s.id, label: s.nome }))}
                  />

                  <span style={{ color: 'var(--color-placeholder)', fontSize: 11 }}>
                    {intervention.durata_stimata}h est.
                    {intervention.ore_effettive ? ` · ${intervention.ore_effettive}h eff.` : ''}
                  </span>

                  {(intervention as any).workshop_nome && (
                    <Tag
                      color="orange"
                      icon={<ApartmentOutlined />}
                      style={{ fontSize: 11, cursor: canAssign ? 'pointer' : 'default' }}
                      onClick={canAssign ? () => {
                        setAssignWorkshopModal({ workOrderId, interventionId: intervention.id!, currentWorkshopId: (intervention as any).workshop_id ?? null, interventions })
                      } : undefined}
                    >
                      {(intervention as any).workshop_nome}
                    </Tag>
                  )}
                </Space>

                {/* Note */}
                {intervention.note_intervento && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    📝 {intervention.note_intervento}
                  </div>
                )}
                {intervention.note_sospensione && (
                  <div style={{ fontSize: 12, color: token.colorWarning, marginTop: 4 }}>
                    Intervento sospeso: {intervention.note_sospensione}
                  </div>
                )}

                {/* Nota RDA */}
                {rdaMap[intervention.id!]?.note && (
                  <div style={{
                    marginTop: 6,
                    padding: '6px 10px',
                    background: token.colorWarningBg,
                    border: `1px solid ${token.colorWarningBorder}`,
                    borderRadius: token.borderRadiusSM,
                    fontSize: 12,
                    fontWeight: 700,
                    color: token.colorWarning,
                    lineHeight: 1.5,
                  }}>
                    📝 {rdaMap[intervention.id!].note}
                  </div>
                )}

                {/* RDA associate all'intervento */}
                {intervention.purchase_requests && intervention.purchase_requests.length > 0 && (
                  <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid var(--color-border)' }}>
                    {intervention.purchase_requests.map((rda: PurchaseRequest) => {
                      return (
                        <div
                          key={`rda-${rda.id}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 8px',
                            backgroundColor: 'var(--color-bg-subtle)',
                            borderRadius: 4,
                            marginBottom: 4,
                            fontSize: 12,
                          }}
                        >
                          <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                            {(rda as any).purchase_request_status_nome ?? `Stato ${rda.purchase_request_status_id}`}
                          </Tag>
                          <TagArticoliResi quanti={(rda as any).articoli_resi} compatto />
                          <span style={{ fontWeight: 500 }}>{rda.codice}</span>
                          <span style={{ color: 'var(--color-text-secondary)' }}>{rda.fornitore || rda.supplier_nome || '—'}</span>
                          <span style={{ color: token.colorPrimary }}>
                            €{Number(rda.costo_stimato_totale || 0).toFixed(2)}
                          </span>
                          {rda.descrizione && (
                            <span style={{ color: 'var(--color-placeholder)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {rda.descrizione}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Azioni */}
              <Space size={4}>
                {canAssign && (
                  <Tooltip title={`Assegna a officina${(intervention as any).workshop_nome ? ` (attuale: ${(intervention as any).workshop_nome})` : ''}`}>
                    <Button
                      type="text"
                      size="small"
                      icon={<ApartmentOutlined />}
                      style={(intervention as any).workshop_id ? { color: token.colorWarning } : {}}
                      onClick={() => {
                        setAssignWorkshopModal({ workOrderId, interventionId: intervention.id!, currentWorkshopId: (intervention as any).workshop_id ?? null, interventions })
                      }}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Modifica">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditIntervention(intervention, workOrderId)}
                  />
                </Tooltip>
                {!isWorkshopRole && (
                  <Popconfirm
                    title="Eliminare questo intervento?"
                    onConfirm={() => intervention.id && handleDeleteIntervention(intervention.id, workOrderId)}
                    okText="Sì"
                    cancelText="No"
                  >
                    <Tooltip title="Elimina">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            </div>
          </div>
        ))}
        
        {/* Pulsante aggiungi */}
        {!isWorkshopRole && (
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleCreateIntervention(workOrderId)}
            style={{ marginTop: 4 }}
          >
            Aggiungi Intervento
          </Button>
        )}
      </div>
    )
  }

  if (workOrdersLoading) {
    return (
      <Card title="🔧 Schede Lavoro e Interventi">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin size="large" tip="Caricamento schede lavoro..." />
        </div>
      </Card>
    )
  }

  return (
    <Card 
      title={
        <Space>
          <ToolOutlined />
          <span>Schede Lavoro e Interventi</span>
          <Tag color="blue">{filteredWorkOrders.length}</Tag>
        </Space>
      }
      extra={
        <Space wrap>
          {/* Filtro data in testata */}
          <DatePicker
            value={dateFilter}
            onChange={setDateFilter}
            placeholder="Filtra per data"
            format="DD/MM/YYYY"
            size="small"
            allowClear
          />
          <Button 
            size="small"
            type={dateFilter && dateFilter.isSame(dayjs(), 'day') ? 'primary' : 'default'}
            onClick={() => setDateFilter(dayjs())}
          >
            Oggi
          </Button>
          <Button 
            icon={<FilterOutlined />} 
            onClick={() => setShowFilters(!showFilters)}
            type={showFilters ? 'primary' : 'default'}
            size="small"
          >
            Altri Filtri
          </Button>
          <Button icon={<DownloadOutlined />} onClick={expandAll} size="small">
            Espandi
          </Button>
          <Button icon={<UploadOutlined />} onClick={collapseAll} size="small">
            Riduci
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetchWorkOrders()} size="small">
            Ricarica
          </Button>
        </Space>
      }
    >
      {/* Filtri */}
      {showFilters && (
        <div style={{ marginBottom: 16, padding: 12, background: 'var(--color-bg-subtle)', borderRadius: 6 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Cerca (scheda, cliente, targa...)"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Stato scheda"
                value={statusFilter}
                onChange={setStatusFilter}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="bozza">Bozza</Select.Option>
                <Select.Option value="in approvazione">In Approvazione</Select.Option>
                <Select.Option value="approvata">Approvata</Select.Option>
                <Select.Option value="in lavorazione">In Lavorazione</Select.Option>
                <Select.Option value="sospesa">Sospesa</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button 
                onClick={() => {
                  setSearchText('')
                  setStatusFilter(undefined)
                  setDateFilter(null)
                }}
                style={{ width: '100%' }}
              >
                Pulisci filtri
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {/* Lista schede lavoro */}
      {filteredWorkOrders.length === 0 ? (
        <Empty description={
          dateFilter 
            ? `Nessuna scheda con interventi per il ${dateFilter.format('DD/MM/YYYY')}`
            : "Nessuna scheda lavoro con interventi trovata"
        } />
      ) : (
        <Collapse
          activeKey={expandedKeys}
          onChange={handleCollapseChange}
          items={filteredWorkOrders.map(workOrder => {
            const details = workOrderDetails.get(workOrder.id)
            const interventionsCount = details?.interventions?.length || 0
            
            return {
              key: workOrder.id.toString(),
              label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '12px' }}>
                  <Space size="small" style={{ flex: 1 }}>
                    <span style={{ fontSize: '12px', color: token.colorPrimary, fontWeight: 500, minWidth: '75px' }}>
                      {dayjs(workOrder.data_appuntamento || workOrder.data_creazione).format('DD/MM/YYYY')}
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: 500, minWidth: '90px' }}>
                      {workOrder.numero_scheda}
                    </span>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                      {workOrder.vehicle_targa}
                    </span>
                    <span style={{ fontSize: '13px', color: 'var(--color-placeholder)' }}>
                      {workOrder.customer_nome}
                    </span>
                    {interventionsCount > 0 && (
                      <Tag color="blue" style={{ marginLeft: 4 }}>
                        {interventionsCount} int.
                      </Tag>
                    )}
                  </Space>
                  <Space>
                    <Tag color={statusColors[workOrder.stato] || 'default'} style={{ marginRight: 0, fontSize: '11px' }}>
                      {workOrder.stato}
                    </Tag>
                    <Tooltip title="Apri scheda lavoro completa">
                      <Button
                        type="text"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={(e) => {
                          e.stopPropagation()
                          goToWorkOrder(workOrder.id)
                        }}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ),
              children: renderWorkOrderDetail(workOrder.id),
            }
          })}
        />
      )}

      {/* Modal conferma gestione RDA quando si cambia modalità da Acquisto */}
      <Modal
        title="⚠️ RDO collegate all'intervento"
        open={pendingInterventionSave !== null}
        onCancel={() => setPendingInterventionSave(null)}
        footer={[
          <Button key="cancel" onClick={() => setPendingInterventionSave(null)}>
            Annulla
          </Button>,
          <Button
            key="unlink"
            type="primary"
            onClick={async () => {
              const linkedRdas = (editingIntervention as any)?.purchase_requests || []
              try {
                await Promise.all(
                  linkedRdas.map((rda: PurchaseRequest) =>
                    axiosInstance.patch(`/purchase-requests/${rda.id}`, { intervention_id: null })
                  )
                )
                await doSaveIntervention(pendingInterventionSave)
              } catch {
                message.error('Errore durante la gestione delle RDO')
              } finally {
                setPendingInterventionSave(null)
              }
            }}
          >
            Mantieni RDO (senza intervento)
          </Button>,
          <Button
            key="delete"
            danger
            onClick={async () => {
              const linkedRdas = (editingIntervention as any)?.purchase_requests || []
              try {
                await Promise.all(
                  linkedRdas.map((rda: PurchaseRequest) =>
                    axiosInstance.delete(`/purchase-requests/${rda.id}`)
                  )
                )
                await doSaveIntervention(pendingInterventionSave)
              } catch {
                message.error('Errore durante l\'eliminazione delle RDO')
              } finally {
                setPendingInterventionSave(null)
              }
            }}
          >
            Cancella RDO
          </Button>,
        ]}
      >
        {(() => {
          const linkedRdas = (editingIntervention as any)?.purchase_requests || []
          return (
            <div>
              <p>
                Stai cambiando la modalità dell'intervento da <strong>Acquisto</strong> a <strong>Riparazione</strong>.
              </p>
              <p>L'intervento ha {linkedRdas.length} RDO collegata/e:</p>
              <ul>
                {linkedRdas.map((rda: PurchaseRequest) => (
                  <li key={rda.id}>
                    <strong>{rda.codice}</strong> — {rda.fornitore || rda.supplier_nome || '—'}
                  </li>
                ))}
              </ul>
              <p>Cosa vuoi fare con le RDO collegate?</p>
            </div>
          )
        })()}
      </Modal>

      {/* Modal Nuovo Intervento (con catalogo) */}
      <NuovoInterventoModal
        open={isNuovoInterventoOpen}
        onClose={() => setIsNuovoInterventoOpen(false)}
        onConfirm={handleNuovoInterventoConfirm}
        canEditSinistro={canEditSinistro}
      />

      {/* Modal Modifica Intervento */}
      <Modal
        title="Modifica Intervento"
        open={isEditModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsEditModalOpen(false)}
        width="min(600px, 95vw)"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveIntervention}
        >
          <Form.Item
            name="descrizione_intervento"
            label="Descrizione Intervento"
            rules={[{ required: true, message: 'Inserisci la descrizione' }]}
          >
            <VoiceTextarea rows={2} minHeight={70} maxHeight={150} label="Descrizione Intervento" compact />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="tipo_intervento"
                label="Tipo"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="Meccanico">Meccanico</Select.Option>
                  <Select.Option value="Carrozziere">Carrozziere</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="modalita_risoluzione_id"
                label="Modalità"
              >
                <Select placeholder="Seleziona">
                  <Select.Option value={1}>Riparazione</Select.Option>
                  <Select.Option value={2}>Acquisto</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="durata_stimata"
                label="Durata stimata (h)"
                rules={[{ required: true }]}
              >
                <InputNumber min={0.5} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="stato_intervento_id"
                label="Stato Intervento"
              >
                <Select placeholder="Seleziona stato" allowClear>
                  {interventionStatuses.map(s => (
                    <Select.Option key={s.id} value={s.id}>{s.nome}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ore_effettive"
                label="Ore Effettive"
              >
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="note_intervento"
            label="Note"
          >
            <VoiceTextarea rows={2} minHeight={70} maxHeight={150} label="Note intervento" compact />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!notaModal}
        title="Motivo richiesto"
        okText="Conferma"
        cancelText="Annulla"
        onOk={handleNotaConfirm}
        onCancel={() => setNotaModal(null)}
      >
        <Input.TextArea
          rows={3}
          placeholder="Inserisci il motivo..."
          value={notaText}
          onChange={e => setNotaText(e.target.value)}
          autoFocus
        />
      </Modal>

      {rdaAfterNew.interventionId > 0 && (
        <RdaInterventoModal
          open={rdaAfterNew.open}
          onClose={() => setRdaAfterNew({ open: false, interventionId: 0, workOrderId: 0, descrizione: '', note: null })}
          interventionId={rdaAfterNew.interventionId}
          workOrderId={rdaAfterNew.workOrderId}
          interventionInfo={{ descrizione: rdaAfterNew.descrizione, note: rdaAfterNew.note }}
        />
      )}

      <AssegnaOfficinaModal
        interventionId={assignWorkshopModal?.interventionId ?? 0}
        workOrderId={assignWorkshopModal?.workOrderId ?? 0}
        currentWorkshopId={assignWorkshopModal?.currentWorkshopId ?? null}
        interventions={assignWorkshopModal?.interventions ?? []}
        open={!!assignWorkshopModal}
        onClose={() => setAssignWorkshopModal(null)}
        onSuccess={() => {
          if (!assignWorkshopModal) return
          setWorkOrderDetails(prev => {
            const m = new Map(prev)
            m.delete(assignWorkshopModal.workOrderId)
            return m
          })
          loadWorkOrderDetail(assignWorkshopModal.workOrderId)
        }}
      />
    </Card>
  )
}

export default WorkOrderInterventionsPanel
